import React from 'react';
import { Box, BoxProps, Icon } from 'pounceV2';
import { AIInferenceStreamUsage, RiskScoreEvidence } from 'Generated/schema';
import EnrichedAIResponseV3 from 'Components/AI/EnrichedAIResponseV3';
import useEventHandler from 'Hooks/useEventHandler';
import { useTheme } from '@mui/material';
import useResize from './useResizeTextarea';

type OverflowAIResponseProps = {
  value: string;
  endContent?: React.ReactNode;
  isLoading?: boolean;
  usage?: AIInferenceStreamUsage;
  riskScoreEvidence?: RiskScoreEvidence | null;
} & BoxProps;

const DEFAULT_MAX_HEIGHT = 320;

const OverflowAIResponseV3 = ({ value, ...props }: OverflowAIResponseProps) => {
  const {
    palette: { tokens },
  } = useTheme();
  const resizableRef = React.useRef<HTMLDivElement>(null);
  const { handleMouseDown, GlobalCursorStyles } = useResize(resizableRef);
  const scrollableRef = React.useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const [scrollState, setScrollState] = React.useState({
    isAtTop: true,
    isAtBottom: false,
  });
  const [hasBeenManuallyResized, setHasBeenManuallyResized] = React.useState(false);

  const maxHeight = React.useMemo(() => {
    return hasBeenManuallyResized ? 'none' : `${DEFAULT_MAX_HEIGHT}px`;
  }, [hasBeenManuallyResized]);

  const height = React.useMemo(() => {
    if (!scrollableRef.current || !value.length) {
      return 'auto';
    }
    if (hasBeenManuallyResized) {
      return resizableRef.current?.style.height || `${resizableRef.current.offsetHeight}px`;
    }

    // If content exceeds the default max height, set height to enable scrolling
    // Otherwise, let it auto-size to content
    return scrollableRef.current.scrollHeight > DEFAULT_MAX_HEIGHT
      ? `${DEFAULT_MAX_HEIGHT}px`
      : 'auto';
  }, [hasBeenManuallyResized, value]);

  const handleScrollStateUpdate = React.useCallback(scrollableElement => {
    if (!scrollableElement) {
      return;
    }
    const scrollBottom = scrollableElement.scrollTop + scrollableElement.offsetHeight;
    const _isAtTop = scrollableElement.scrollTop === 0;
    const _isAtBottom = Math.abs(scrollBottom - scrollableElement.scrollHeight) < 2; // Allow 2px tolerance

    setScrollState(prevScrollState => {
      if (_isAtTop !== prevScrollState.isAtTop || _isAtBottom !== prevScrollState.isAtBottom) {
        return {
          isAtTop: _isAtTop,
          isAtBottom: _isAtBottom,
        };
      }
      return prevScrollState;
    });
  }, []);

  // Check overflow on initial render and when value changes
  React.useEffect(() => {
    if (scrollableRef.current) {
      const hasOverflow = scrollableRef.current.scrollHeight > scrollableRef.current.offsetHeight;
      setIsOverflowing(hasOverflow);
      handleScrollStateUpdate(scrollableRef.current);
    }
  }, [value, handleScrollStateUpdate]);

  const observer = React.useMemo(() => {
    return new MutationObserver(mutationList => {
      mutationList.forEach(mutation => {
        if (mutation.target === scrollableRef.current) {
          // This effect will ensure the overflow shadow is rendered again if the user had scrolled
          // to the bottom, then more content came in and the length increased
          handleScrollStateUpdate(scrollableRef.current);
        }
      });

      const _isOverflowing =
        scrollableRef.current?.scrollHeight > scrollableRef.current?.offsetHeight;

      // This will ensure the isOverflowing value is up to date as the outer resizable
      // container is resized
      setIsOverflowing(prevIsOverflowing => {
        if (_isOverflowing !== prevIsOverflowing) {
          return _isOverflowing;
        }

        return prevIsOverflowing;
      });
    });
  }, [handleScrollStateUpdate]);

  React.useEffect(() => {
    // We're observing changes to both the outer resizableRef as well as the scrollableRef
    if (resizableRef.current && scrollableRef.current) {
      observer.observe(resizableRef.current, { attributes: true });
      observer.observe(scrollableRef.current, { attributes: true });
    }
    return () => observer.disconnect();
  }, [observer]);

  const handleScroll = useEventHandler(e => {
    const scrollableElement = e.target;
    handleScrollStateUpdate(scrollableElement);
  });

  const handleResize = e => {
    setHasBeenManuallyResized(true);
    handleMouseDown(e);
  };

  return (
    <Box position="relative">
      {GlobalCursorStyles}
      <Box
        data-testid="resizable-box"
        ref={resizableRef}
        width="100%"
        minHeight="48px"
        height={height}
        maxHeight={maxHeight}
        position="relative"
        borderRadius="large"
        overflow="hidden"
        sx={{
          backgroundColor: tokens.background.neutral.mid[3],
          resize: 'vertical',
          '::-webkit-resizer': {
            display: 'none',
          },
          // Add the overflow styles when needed
          ...(isOverflowing &&
            !scrollState.isAtTop && {
              '::before': {
                content: '""',
                height: 40,
                position: 'absolute',
                top: 0,
                width: '100%',
                background: `linear-gradient(0deg, transparent 0%, ${tokens.background.neutral.mid[3]} 100%)`,
                zIndex: 1,
              },
            }),
          ...(isOverflowing &&
            !scrollState.isAtBottom && {
              '::after': {
                content: '""',
                height: 40,
                position: 'absolute',
                bottom: 0,
                width: '100%',
                background: `linear-gradient(180deg, transparent 0%, ${tokens.background.neutral.mid[3]} 100%)`,
              },
            }),
        }}
        {...props}
      >
        <Box
          ref={scrollableRef}
          height="100%"
          width="100%"
          overflow="auto"
          p="12px 8px 12px 12px"
          whiteSpace="pre-wrap"
          onScroll={handleScroll}
          aria-readonly
          sx={{
            '::-webkit-scrollbar-track': {
              position: 'relative',
              marginBottom: '18px',
              marginLeft: 1,
            },
            '::-webkit-scrollbar-thumb': {
              position: 'relative',
            },
          }}
        >
          <EnrichedAIResponseV3
            response={value}
            finished={!props.isLoading}
            usage={props.usage}
            riskScoreEvidence={props.riskScoreEvidence}
          />
          {props.endContent && <>{props.endContent}</>}
        </Box>
      </Box>

      {/* Custom drag handle at bottom right corner */}
      <Box
        onMouseDown={handleResize}
        sx={{
          position: 'absolute',
          bottom: 3,
          right: 3,
          cursor: 'ns-resize',
        }}
      >
        <Icon type="drag-handle" color={tokens.content.text.accent[14][2]} size="small" />
      </Box>
    </Box>
  );
};

export default OverflowAIResponseV3;
